import {
  FormEvent,
  MouseEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { APP_DOWNLOAD_MODAL_COPY } from "shared/config/appDownloadModalCopy";
import { Dialog, Message, Overlay, Title } from "../ConfirmationModal/styles";
import {
  CancelButton,
  EmailInput,
  ErrorMessage,
  Form,
  Label,
  Lead,
  Notice,
  SubmitButton,
  WhyButton,
  WhyText,
} from "./styles";
import type { AppDownloadEmailModalProps } from "./types";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AppDownloadEmailModal({
  isOpen,
  onEmailChange,
  onSubmit,
  onClose,
}: AppDownloadEmailModalProps) {
  const titleId = useId();
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const whyId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;

    setEmail("");
    setShowError(false);
    setShowWhy(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setShowError(true);
      return;
    }
    onSubmit(email);
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return createPortal(
    <Overlay onMouseDown={handleOverlayClick}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Title id={titleId}>{APP_DOWNLOAD_MODAL_COPY.title}</Title>
        <Message>
          <Lead>{APP_DOWNLOAD_MODAL_COPY.lead}</Lead>
          <Form noValidate onSubmit={handleSubmit}>
            <Label htmlFor={inputId}>
              {APP_DOWNLOAD_MODAL_COPY.emailLabel}
            </Label>
            <EmailInput
              ref={inputRef}
              id={inputId}
              type="email"
              autoComplete="email"
              value={email}
              aria-invalid={showError}
              aria-describedby={showError ? errorId : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
                onEmailChange?.(event.target.value);
              }}
            />
            {showError && (
              <ErrorMessage id={errorId} role="alert">
                {APP_DOWNLOAD_MODAL_COPY.emailError}
              </ErrorMessage>
            )}
            <WhyButton
              type="button"
              aria-expanded={showWhy}
              aria-controls={whyId}
              onClick={() => setShowWhy((open) => !open)}
            >
              {APP_DOWNLOAD_MODAL_COPY.whyLabel}
            </WhyButton>
            {showWhy && (
              <WhyText id={whyId}>{APP_DOWNLOAD_MODAL_COPY.whyText}</WhyText>
            )}
            <SubmitButton type="submit">
              {APP_DOWNLOAD_MODAL_COPY.submitLabel}
            </SubmitButton>
            <Notice>
              {APP_DOWNLOAD_MODAL_COPY.consentPrefix}{" "}
              <a
                href={APP_DOWNLOAD_MODAL_COPY.termsUrl}
                target="_blank"
                rel="noreferrer"
              >
                {APP_DOWNLOAD_MODAL_COPY.termsLabel}
              </a>{" "}
              {APP_DOWNLOAD_MODAL_COPY.consentJoin}{" "}
              <a
                href={APP_DOWNLOAD_MODAL_COPY.privacyUrl}
                target="_blank"
                rel="noreferrer"
              >
                {APP_DOWNLOAD_MODAL_COPY.privacyLabel}
              </a>
              {"."}
            </Notice>
            <CancelButton type="button" onClick={onClose}>
              {APP_DOWNLOAD_MODAL_COPY.cancelLabel}
            </CancelButton>
          </Form>
        </Message>
      </Dialog>
    </Overlay>,
    document.body,
  );
}

export type { AppDownloadEmailModalProps } from "./types";
